<template>
  <div>
    <h2>
      <span class="form-group">
        Trace Elements / PP+
        <comments field-name="heading_trace_elements_pp" :isReadOnly="isReadOnly"></comments>
      </span>
    </h2>
		<div class="alert alert-success text-center">
			This step is optional.
		</div>

    <h3 class="text-left">
      <span class="form-group">
        Serum MG
        <comments field-name="heading_serum_mg" :isReadOnly="isReadOnly"></comments>
      </span>
    </h3>
    <table class="table table-bordered">
      <thead>
        <tr>
          <th>Date</th>
          <th>Animal Name</th>
          <th>Description</th>
          <th>Test Type</th>
          <th>avg</th>
          <th># &lt; 0.8</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(t_mg, index) in getChartData('ter_mg_results')" :key="index">
          <td class="text-left">{{ t_mg['Date'] }}</td>
          <td class="text-left">{{ t_mg['Animal Name'] }}</td>
          <td class="text-left">{{ t_mg['Description'] }}</td>
          <td class="text-left">{{ t_mg['Test Type'] }}</td>
          <td class="text-left">{{ t_mg['Average'] }}</td>
          <td class="text-left">{{ t_mg['# &lt; 0.8'] }}</td>
        </tr>
      </tbody>
    </table>

    <box-plot-chart :chart-data="get_mg_data" />

    <h3 class="text-left">
      <span class="form-group">
        Serum SE
        <comments field-name="heading_serum_se" :isReadOnly="isReadOnly"></comments>
      </span>
    </h3>
    <table class="table table-bordered">
      <thead>
        <tr>
          <th>Date</th>
          <th>Animal Name</th>
          <th>Description</th>
          <th>Test Type</th>
          <th>avg</th>
          <th># &lt; 500</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(t_se, index) in getChartData('ter_se_results')" :key="index">
          <td class="text-left">{{ t_se['Date'] }}</td>
          <td class="text-left">{{ t_se['Animal Name'] }}</td>
          <td class="text-left">{{ t_se['Description'] }}</td>
          <td class="text-left">{{ t_se['Test Type'] }}</td>
          <td class="text-left">{{ t_se['Average'] }}</td>
          <td class="text-left">{{ t_se['# &lt; 500'] }}</td>
        </tr>
      </tbody>
    </table>

    <box-plot-chart :chart-data="get_se_data" />

    <h3 class="text-left">
      <span class="form-group">
        Liver CU
        <comments field-name="heading_liver_cu" :isReadOnly="isReadOnly"></comments>
      </span>
    </h3>
    <table class="table table-bordered">
      <thead>
        <tr>
          <th>Date</th>
          <th>Animal Name</th>
          <th>Description</th>
          <th>Test Type</th>
          <th>avg</th>
          <th># &lt; 500</th>
          <th># &gt; 3000</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(t_cu, index) in getChartData('ter_cu_results')" :key="index">
          <td class="text-left">{{ t_cu['Date'] }}</td>
          <td class="text-left">{{ t_cu['Animal Name'] }}</td>
          <td class="text-left">{{ t_cu['Description'] }}</td>
          <td class="text-left">{{ t_cu['Test Type'] }}</td>
          <td class="text-left">{{ t_cu['Average'] }}</td>
          <td class="text-left">{{ t_cu['# &lt; 500'] }}</td>
          <td class="text-left">{{ t_cu['# &gt; 3000'] }}</td>
        </tr>
      </tbody>
    </table>

    <box-plot-chart :chart-data="get_cu_data" />

    <programme-input programme-slug="ppp"></programme-input>

    <div class="clearfix" :style="{ width: widthForRow(getFieldValue('FARMS')) + 'px' }">
      <div v-for="farm in FARMS" class="farm-fields" :key="farm.id">
        <h3>{{ farm.farm_label }}</h3>

        <radio-input
          label="One-off trace element testing"
          :field-name="`farm_${farm.id}-one_off_te_test`"
          :choices="[
            { value: 'Yes', display: 'Yes' },
            { value: 'No', display: 'No' },
          ]"
          :disabled="isReadOnly"
        >
        </radio-input>

        <radio-input
          label="Custom Mineral Mix?"
          :field-name="`farm_${farm.id}-te_ppp_custom_min_mix`"
          :addEmailNotification="true"
          :addComment="true"
          :choices="[
            { value: 'Yes', display: 'Yes' },
            { value: 'No', display: 'No' },
          ]"
          :disabled="isReadOnly"
        >
        </radio-input>

        <discussion-points
          title="What were your metabolics like this season?"
          :comments-field="`farm_${farm.id}-metabolics_comments`"
          points="Number of down cows, mobs, what did you give them (e.g. Ca, Mg, etc)"
        >
        </discussion-points>

        <radio-input
          label="Interested in transition minerals?"
          :field-name="`farm_${farm.id}-wants_agvance_trans_salts`"
          :addEmailNotification="true"
          :addComment="true"
          :choices="[
            { value: 'Yes', display: 'Yes' },
            { value: 'No', display: 'No' },
          ]"
          :disabled="isReadOnly"
        >
        </radio-input>

        <radio-input
          label="Interested in Rumenox?"
          :field-name="`farm_${farm.id}-wants_rumenox`"
          :addEmailNotification="true"
          :addComment="true"
          :choices="[
            { value: 'Yes', display: 'Yes' },
            { value: 'No', display: 'No' },
            { value: 'Maybe', display: 'Maybe' },
          ]"
          :disabled="isReadOnly"
        >
        </radio-input>

        <radio-input
          label="Do you want an energy watch visit?"
          :field-name="`farm_${farm.id}-wants_energy_watch_visit`"
          :choices="[
            { value: 'Yes', display: 'Yes' },
            { value: 'No', display: 'No' },
            { value: 'Maybe', display: 'Maybe' },
          ]"
          :disabled="isReadOnly"
        >
        </radio-input>
      </div>
    </div>

    <drug-allocation-cart :farms="getFieldValue('FARMS')" :drugs="getFieldValue('DRUGS')" :isReadOnly="isReadOnly">
    </drug-allocation-cart>
  </div>
</template>

<script>
import baseComponent from '@/mixins/base_consult_component_v2'
import DrugAllocationCart from './DrugAllocationCart'
import BoxPlotChart from '../charts/BoxPlotChart'

export default {
  name: 'TraceElements',
  mixins: [ baseComponent ],
  components: {
    DrugAllocationCart,
    BoxPlotChart,
  },
  methods: {
    getLabels(data) {
        return data.map(row => row['Date'])
    },
    getData(data) {
      return data.map(function(row) {
        let res = row['res']
        if (res) {
          return res.split(', ').map(n => parseFloat(n))
        }
      })
    },
    toggleNotifications: function() {
      let notificationFields = {
        te_ppp_custom_min_mix: {slug: 'custom-mineral-mix-order', compareTo: ['Yes']},
        wants_rumenox: {slug: 'wants-rumenox', compareTo: ['Yes', 'Maybe']},
        wants_agvance_trans_salts: {slug: 'agvance-trans-salts', compareTo: ['Yes']},
      }

      for (let [fieldName, fieldAttrs] of Object.entries(notificationFields)) {
        let fieldsToCheck = this.FARMS.map((farm) => this.prefixedField(fieldName, farm.id))
        this.setEmailNotification({
          slug: fieldAttrs.slug,
          value: fieldsToCheck.some((field) =>
            fieldAttrs.compareTo.includes(this.getFieldValue(field, this.$options.name))
          )
        })
      }
    }
  },
  computed: {
    get_mg_data: function () {
        return {
            labels: this.getLabels(this.getChartData('ter_mg_results')),
            datasets: [
                {
                    label: "Serum MG",
                    borderWidth: 2,
                    borderColor: 'rgba(0, 0, 0, .4)',
                    backgroundColor: "rgba(51, 122, 183, 0.6)",
                    data: this.getData(this.getChartData('ter_mg_results')),
                },
            ]
        }
    },
    get_se_data: function () {
        return {
            labels: this.getLabels(this.getChartData('ter_se_results')),
            datasets: [
                {
                    label: "Serum SE",
                    borderWidth: 2,
                    borderColor: 'rgba(0, 0, 0, .4)',
                    backgroundColor: "rgba(51, 122, 183, 0.6)",
                    data: this.getData(this.getChartData('ter_se_results')),
                },
            ]
        }
    },
    get_cu_data: function () {
        return {
            labels: this.getLabels(this.getChartData('ter_cu_results')),
            datasets: [
                {
                    label: "Liver CU",
                    borderWidth: 2,
                    borderColor: 'rgba(0, 0, 0, .4)',
                    backgroundColor: "rgba(51, 122, 183, 0.6)",
                    data: this.getData(this.getChartData('ter_cu_results')),
                },
            ]
        }
    },
  }
};
</script>

<style>
.farm-fields {
  float: left;
  width: 500px;
  margin-right: 3em;
}
</style>
